/* The as-of view's read model (R3b, Ale rulings 2026-09-04).
 *
 * The engine serves the record as it was known at a date — ONE rule,
 * `funds/as_of_scope`: a holding is in the D view iff it has a stated
 * balance dated ≤ D or a dated confirmed movement ≤ D; its figure is the
 * latest balance ≤ D rolled forward with the movements up to D, or the
 * sum of the movements when no statement exists ("from movements"). A
 * holding whose only evidence is dated after D has NO balance as of D.
 *
 * This module holds what the frontend adds on top of that payload:
 *   · the dropped holdings resolved back to the FULL record's legs, so the
 *     diagram can draw them greyed IN PLACE ("no balance as of 31 May
 *     2026 — first evidence 30 Jun 2026") rather than vanish them;
 *   · the first-evidence date of such a leg, derived client-side from the
 *     full record (R3a's dropped rows carry the reason only);
 *   · the calendar's month grid for the picker.
 *
 * Pure and import-free so the bare node runner proves it — see
 * tests/unit/asOf.test.mjs. Every date is ISO (YYYY-MM-DD) in here; the
 * components format for the officer (formatOfficerDate) at render. */

import type { Dropped, RbSegment, RbTable, RbTrade } from './rebuildRecord'

export type BalanceSource = 'balance' | 'balance+trades' | 'trades'

/** The flags a reader of the D view must honour (as_of_scope.notes). */
export interface AsOfNotes {
  rule?: string
  /** "latest_known": issued totals are today's — no dated supply history. */
  supply_as_of?: string
  /** "current": the proof status is as it stands today, not as of D. */
  evidence_as_of?: string
  documents?: string
  commitment_legs?: string
}

/** GET /api/funds/{ref}/as-of?date=D — the top-level shape, exactly. */
export interface AsOfPayload<T = unknown, M = unknown> {
  as_of: string
  record_table: T
  matrix: { rows: M[] }
  dropped: Dropped[]
  notes: AsOfNotes
}

/** A holding the D view left out, resolved to its leg on the full record. */
export interface DroppedLeg<S extends RbSegment = RbSegment> {
  seg: S
  reason: string
  /** The earliest dated item on the leg in the full record — the date its
   *  evidence begins. null when nothing on it is dated. */
  firstEvidence: string | null
  /** Confirmed movements on the leg dated after D (or undated). */
  tradesAfter: number
}

const confirmed = (state: string): boolean => state !== 'proposed'

function legIds(seg: RbSegment): string[] {
  return seg.position_ids.length ? seg.position_ids : [seg.position_id]
}

function legTrades(seg: RbSegment, trades: RbTrade[]): RbTrade[] {
  const ids = new Set(legIds(seg))
  return trades.filter((t) => confirmed(t.state) && !!t.position_id && ids.has(t.position_id))
}

/** The earliest dated evidence on a leg of the FULL record: its live
 *  balance date or its earliest dated confirmed movement, whichever is
 *  first. A zero-engine fallback for R3a's dropped rows, which name the
 *  reason but not the date the holding's evidence begins. */
export function firstEvidenceOf(seg: RbSegment, trades: RbTrade[]): string | null {
  const dates: string[] = []
  if (seg.as_of) dates.push(seg.as_of)
  for (const t of legTrades(seg, trades)) if (t.effective_date) dates.push(t.effective_date)
  if (dates.length === 0) return null
  return dates.sort()[0]
}

/** The dropped POSITIONS of a D view, each resolved to its leg on the full
 *  record (the D view no longer carries it). Entities and instruments the
 *  D view left out simply vanish with their legs — R3a drops only
 *  positions by name. Order follows the full record's legs. */
export function droppedLegsOf<S extends RbSegment>(
  full: RbTable & { segments: S[] },
  dropped: Dropped[],
  asOf: string
): DroppedLeg<S>[] {
  const ids = new Map<string, string>()
  for (const d of dropped) if (d.kind === 'position') ids.set(d.id, d.reason)
  const out: DroppedLeg<S>[] = []
  for (const seg of full.segments) {
    if (seg.group !== 'chain') continue
    const hit = legIds(seg).find((id) => ids.has(id))
    if (!hit) continue
    const moves = legTrades(seg, full.trades)
    out.push({
      seg,
      reason: ids.get(hit) as string,
      firstEvidence: firstEvidenceOf(seg, full.trades),
      tradesAfter: moves.filter((t) => !t.effective_date || t.effective_date > asOf).length,
    })
  }
  return out
}

/** The as-of view's tally of how the balances were fixed, for the chain
 *  status source line: the statement dates the figures rest on, the legs
 *  fixed by movements only, and the holdings without a balance. */
export interface BalanceSources<S extends RbSegment = RbSegment> {
  /** Distinct balance dates of the legs fixed by a statement, ascending. */
  statementDates: string[]
  /** Legs whose figure is the sum of movements (no statement ≤ D). */
  fromMovements: S[]
}

export function balanceSourcesOf<S extends RbSegment>(chain: S[]): BalanceSources<S> {
  const dates = new Set<string>()
  const fromMovements: S[] = []
  for (const s of chain) {
    if (s.balance_source === 'trades') fromMovements.push(s)
    else if (s.as_of) dates.add(s.as_of)
  }
  return { statementDates: [...dates].sort(), fromMovements }
}

/** Demands the D view leaves outside its denominator: rows of the full
 *  matrix whose object is not in the D view, counted and named by object
 *  (the names resolved by the caller's lookup). */
export function outsideDemands(
  full: { object_id: string }[],
  view: { object_id: string }[],
  nameOf: (objectId: string) => string | null
): { count: number; names: string[] } {
  const inView = new Set(view.map((r) => r.object_id))
  const names = new Set<string>()
  let count = 0
  for (const r of full) {
    if (inView.has(r.object_id)) continue
    count += 1
    const n = nameOf(r.object_id)
    if (n) names.add(n)
  }
  return { count, names: [...names] }
}

/* ── the calendar ────────────────────────────────────────────────────── */

export const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
]

const pad2 = (n: number) => String(n).padStart(2, '0')

/** "YYYY-MM" of an ISO date. */
export function monthOf(iso: string): string {
  return iso.slice(0, 7)
}

/** The month `delta` months from "YYYY-MM". */
export function shiftMonth(ym: string, delta: number): string {
  const y = Number(ym.slice(0, 4))
  const m = Number(ym.slice(5, 7)) - 1 + delta
  const yy = y + Math.floor(m / 12)
  const mm = ((m % 12) + 12) % 12
  return `${yy}-${pad2(mm + 1)}`
}

/** "May 2026" for the popover's header. */
export function monthLabel(ym: string): string {
  return `${MONTH_NAMES[Number(ym.slice(5, 7)) - 1]} ${ym.slice(0, 4)}`
}

export interface GridDay {
  iso: string
  day: number
  /** false for the leading/trailing days of the neighbouring months. */
  inMonth: boolean
}

/** The month as whole weeks, Monday first — six rows at most, each of
 *  seven ISO dates, the neighbouring months' days filling the edges. */
export function monthGrid(ym: string): GridDay[][] {
  const y = Number(ym.slice(0, 4))
  const m = Number(ym.slice(5, 7)) - 1
  const first = new Date(Date.UTC(y, m, 1))
  // JS: 0 = Sunday. Monday-first offset: Mon 0 … Sun 6.
  const lead = (first.getUTCDay() + 6) % 7
  const start = new Date(Date.UTC(y, m, 1 - lead))
  const weeks: GridDay[][] = []
  for (let w = 0; w < 6; w += 1) {
    const row: GridDay[] = []
    for (let d = 0; d < 7; d += 1) {
      const dt = new Date(start.getTime() + (w * 7 + d) * 86_400_000)
      const iso = `${dt.getUTCFullYear()}-${pad2(dt.getUTCMonth() + 1)}-${pad2(dt.getUTCDate())}`
      row.push({ iso, day: dt.getUTCDate(), inMonth: dt.getUTCMonth() === m })
    }
    weeks.push(row)
    // Stop once a full week past the month's end has been laid.
    if (row[6].iso.slice(0, 7) > ym && row.every((x) => !x.inMonth)) {
      weeks.pop()
      break
    }
  }
  return weeks
}

/** Is `iso` a well-formed calendar date the URL may carry? */
export function isIsoDate(iso: string | null | undefined): iso is string {
  if (!iso || !/^\d{4}-\d{2}-\d{2}$/.test(iso)) return false
  const y = Number(iso.slice(0, 4))
  const m = Number(iso.slice(5, 7))
  const d = Number(iso.slice(8, 10))
  if (m < 1 || m > 12 || d < 1) return false
  const days = new Date(Date.UTC(y, m, 0)).getUTCDate()
  return d <= days
}
